import React from "react";
import { useTranslation } from "react-i18next";
import EAModal from "../../../../shared/Modal/EAModal";
import EAButton from "../../../../shared/Buttons/EAButton";
import EAAlert from "../../../../shared/EAAlert";
import EAInfoTile from "../../../../shared/InfoTile/EAInfoTile";
import { Ticket } from "../../../../../models/Ticket";
import useLocaleByLanguage from "../../../../../../utils/hooks/useLocaleByLanguage";
import ChangeSwimmingTermSlots from "../SingleReservation/ChangeSwimmingTerm/ChangeSwimmingTermSlots";
import {
  formatSwimmingTermDayLabel,
  formatTimeRange,
} from "../SingleReservation/ChangeSwimmingTerm/formatSwimmingTermLabel";
import useCatchUpLessonsModal from "./useCatchUpLessonsModal";
import "../SingleReservation/ReservationModal.scss";
import "./CatchUpLessons.scss";

const toMs = (value: number): number => (value < 1e11 ? value * 1000 : value);

interface IProps {
  isOpen: boolean;
  ticket: Ticket;
  onClose: () => void;
  onBooked: () => void;
}

const CatchUpLessonsModal: React.FC<IProps> = ({ isOpen, ticket, onClose, onBooked }) => {
  const { t } = useTranslation();
  const locale = useLocaleByLanguage();
  const {
    view,
    groupOptions,
    selectedGroupId,
    setSelectedGroupId,
    referenceExercise,
    selectedLesson,
    error,
    isSubmitting,
    handleSelectLesson,
    handleBack,
    handleConfirm,
  } = useCatchUpLessonsModal({ isOpen, ticket, onClose, onBooked });

  const title = view === "slots" ? "Wybierz termin lekcji" : "Potwierdź termin lekcji";
  const activeGroupLabel = groupOptions.find((group) => group.groupId === selectedGroupId)?.label;
  const stepLabel =
    view === "slots"
      ? `Krok 1 z 2${activeGroupLabel ? ` · ${activeGroupLabel}` : ""}`
      : `Krok 2 z 2${activeGroupLabel ? ` · ${activeGroupLabel}` : ""}`;

  return (
    <EAModal
      isOpen={isOpen}
      title={title}
      onClose={onClose}
      onBack={view === "confirm" ? handleBack : undefined}
      hideButtons
      desktopWidth="600px"
    >
      <div className="reservation-modal catch-up-lessons-modal">
        {view === "slots" && !referenceExercise && (
          <EAAlert
            type="warning"
            message="Nie mamy żadnego terminu na tym karnecie, po którym moglibyśmy dobrać grupę zajęciową — skontaktuj się z obiektem, żeby ustalić lekcję do odrobienia."
          />
        )}

        {view === "slots" && referenceExercise && (
          <>
            <span className="reservation-modal__step-label">{stepLabel}</span>
            {error && <EAAlert type="error" message={error} />}

            {groupOptions.length > 1 && (
              <div className="catch-up-lessons-modal__group-switch">
                {groupOptions.map((group) => (
                  <button
                    key={group.groupId}
                    type="button"
                    className={`catch-up-lessons-modal__group-pill${
                      group.groupId === selectedGroupId ? " catch-up-lessons-modal__group-pill--active" : ""
                    }`}
                    onClick={() => setSelectedGroupId(group.groupId)}
                  >
                    {group.label}
                  </button>
                ))}
              </div>
            )}

            <EAAlert
              type="gray"
              message="Wybierz nowy termin — to zajęcia w zamian za wcześniej zgłoszoną nieobecność."
            />

            <ChangeSwimmingTermSlots
              exercise={referenceExercise}
              ticket={ticket}
              enabled={isOpen}
              onSelectLesson={handleSelectLesson}
            />
          </>
        )}

        {view === "confirm" && selectedLesson && (
          <>
            <span className="reservation-modal__step-label">{stepLabel}</span>
            {error && <EAAlert type="error" message={error} />}

            <div className="reservation-modal__current-term">
              <EAInfoTile date={new Date(toMs(selectedLesson.startTime))} size="medium" color="primary" />
              <div className="reservation-modal__current-term-content">
                <span className="body-text-small text-color-light-gray">TERMIN LEKCJI DO ODROBIENIA</span>
                <b>
                  {formatSwimmingTermDayLabel(selectedLesson.startTime, locale)},{" "}
                  {formatTimeRange(selectedLesson.startTime, selectedLesson.closeTime)}
                </b>
                {selectedLesson.location && (
                  <span className="body-text-small text-color-light-gray">{selectedLesson.location}</span>
                )}
              </div>
            </div>

            <p className="body-text-small text-color-dark-gray" style={{ margin: 0 }}>
              Po potwierdzeniu zapiszemy Cię na ten termin, żeby odrobić zaległe zajęcia.
            </p>

            <div className="reservation-modal__actions">
              <EAButton
                title="Potwierdź termin"
                onClick={handleConfirm}
                fullWidth
                loading={isSubmitting}
                loadingText={t("zapisywanie") || "Zapisywanie..."}
              />
              <EAButton
                title="Wróć do listy"
                onClick={handleBack}
                variant="text"
                fullWidth
                disabled={isSubmitting}
              />
            </div>
          </>
        )}
      </div>
    </EAModal>
  );
};

export default CatchUpLessonsModal;
